import { useEffect, useState } from "react";
import axios from "axios";
import { Alert, FormControl, TextField, Typography } from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../common/appStyles";
import {
  AdditionalStatusCodeEnum,
  getAdditionalCostStatusName,
} from "../../common/EntitiesManagement";
import Combobox from "../../components/common/Combobox";
import localize from "../../common/localize";
import useGlobalContext from "../../hooks/useGlobalContext";
import {
  getApprovedRejectedStatusChangesOptions,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../common/utils";

export default function AddOrEditAdditionalCost({
  model,
  setModel,
  setStatus,
}) {
  const { ADDITIONAL_COST_TYPE_ENDPOINT, config } = useGlobalContext();
  const [descriptionCount, setDescriptionCount] = useState(0);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [selectedStatus, setSelectedStatus] = useState({
    Id: model.Status,
    Name: getAdditionalCostStatusName(model.Status),
  });
  const [additionalCostTypes, setAdditionalCostTypes] = useState([]);

  const getAdditionalCostTypes = async () => {
    const obtainedAdditionalCostTypes = await axios.get(
      `${ADDITIONAL_COST_TYPE_ENDPOINT}`,
      config
    );

    if (
      obtainedAdditionalCostTypes?.data?.Ok &&
      obtainedAdditionalCostTypes?.data?.Data?.length > 0
    ) {
      setAdditionalCostTypes(obtainedAdditionalCostTypes?.data?.Data);
    }
  };

  const getStatusOptions = () => getApprovedRejectedStatusChangesOptions();

  useEffect(() => {
    getAdditionalCostTypes();
  }, []);

  useEffect(() => {
    if (selectedStatus) {
      setModel({
        ...model,
        Status: selectedStatus.Id,
      });
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (model.Observation) {
      setDescriptionCount(model.Observation.length);
    }
  }, [model?.Observation]);

  const handleChange = (e) => {
    setModel({
      ...model,
      [e.target.name]: e.target.value,
    });
  };

  const handleAdditionalCostTypeChange = (value) => {
    setModel({
      ...model,
      AdditionalCostTypeId: !value ? 0 : value.Id,
      AdditionalCostType: !value ? null : value,
      Value: value.DefaultValue,
    });
  };

  const handleAdditionalCostStatusChange = (value) => {
    if (!value) {
      setSelectedStatus({
        Id: 0,
        Name: getAdditionalCostStatusName(0),
      });
      return;
    }

    setSelectedStatus({
      Id: value.Id,
      Name: value.Name,
    });
  };

  return (
    <div style={{ width: "100%" }}>
      <form className="w-full mt-2">
        <div className="flex flex-wrap mb-6">
          <FormControl required fullWidth>
            <Combobox
              isRequired
              disabled={setStatus || additionalCostTypes.length === 0}
              id="AdditionalCostTypeId"
              fieldLabel={localize.additional_cost_type}
              fieldOptions={additionalCostTypes ?? []}
              fieldValue={model.AdditionalCostType}
              handleValueChange={handleAdditionalCostTypeChange}
            />
          </FormControl>
        </div>
        <div className="flex flex-wrap mb-6">
          <TextField
            disabled={setStatus}
            type="number"
            InputLabelProps={getInputLabelProps()}
            inputProps={getInputProps()}
            value={model.Value}
            onChange={handleChange}
            size="small"
            id="Value"
            name="Value"
            label={localize.value}
            variant="outlined"
            fullWidth
          />
        </div>

        {model.value < 0 && (
          <div className="w-full mb-6">
            <Alert className="w-full" severity="warning">
              <Typography className="report_litle_text">
                {localize.warning_value_discounted}
              </Typography>
            </Alert>
          </div>
        )}

        <div className="flex flex-wrap mb-6">
          <FormControl required fullWidth>
            <TextField
              disabled={setStatus}
              InputLabelProps={getInputLabelProps()}
              inputProps={{
                style: {
                  fontSize: "0.8em",
                },
                maxLength: maximumCharactersForDescription,
              }}
              size="small"
              fullWidth
              multiline
              rows={3}
              id="outlined-basic"
              name="Observation"
              label={localize.observations}
              variant="outlined"
              onChange={handleChange}
              value={model.Description}
              helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
            />
          </FormControl>
        </div>

        {setStatus && (
          <div className="w-full flex flex-wrap mb-6">
            <FormControl required fullWidth>
              <Combobox
                id="Status"
                fieldLabel={localize.status}
                fieldOptions={getStatusOptions()}
                fieldValue={selectedStatus}
                handleValueChange={handleAdditionalCostStatusChange}
              />
            </FormControl>
          </div>
        )}

        {setStatus &&
          (selectedStatus?.Id === AdditionalStatusCodeEnum.Approved ? (
            <Alert severity="info" className="w-full">
              <label className="text-xs">
                {localize.info_approved_additional_cost}
              </label>
            </Alert>
          ) : selectedStatus?.Id === AdditionalStatusCodeEnum.Rejected ? (
            <Alert className="w-full" severity="warning">
              <label className="text-xs">
                {localize.warning_rejected_additional_cost}
              </label>
            </Alert>
          ) : (
            <></>
          ))}
      </form>
    </div>
  );
}
